import React from 'react';
import { MapPin, Phone, Mail, Clock, ArrowRight } from 'lucide-react';
import { siteData } from '../data/mock';
import SEO from '../components/SEO';

const ContactPage = () => {
  const contactCards = [
    {
      icon: <MapPin size={22} />,
      title: 'Adresse',
      content: <p className="body-medium">{siteData.contact.address}</p>,
      testId: 'contact-card-address'
    },
    {
      icon: <Phone size={22} />,
      title: 'Téléphone',
      content: (
        <p className="body-medium">
          <a
            href={`tel:${siteData.contact.phone}`}
            style={{ color: 'var(--text-secondary)', textDecoration: 'none' }}
            data-testid="contact-phone-link"
          >
            {siteData.contact.phone}
          </a>
        </p>
      ),
      testId: 'contact-card-phone'
    },
    {
      icon: <Mail size={22} />,
      title: 'Email',
      content: (
        <p className="body-medium">
          <a
            href={`mailto:${siteData.contact.email}`}
            style={{ color: 'var(--text-secondary)', textDecoration: 'none', wordBreak: 'break-word' }}
            data-testid="contact-email-link"
          >
            {siteData.contact.email}
          </a>
        </p>
      ),
      testId: 'contact-card-email'
    },
    {
      icon: <Clock size={22} />,
      title: 'Horaires',
      content: (
        <div
          className="body-medium"
          style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}
        >
          <div>{siteData.contact.hours.weekdays}</div>
          <div>{siteData.contact.hours.saturday}</div>
          <div>{siteData.contact.hours.sunday}</div>
        </div>
      ),
      testId: 'contact-card-hours'
    }
  ];

  return (
    <div style={{ marginTop: '100px' }}>
      <SEO
        title="Contact — Nutri Centre Riviera Vevey & Clarens (Montreux)"
        description="Contactez le Nutri Centre Riviera : téléphone, email, adresses des cabinets de Vevey et Clarens (Montreux). Prise de rendez-vous en ligne disponible 24/7."
        keywords="contact nutritionniste Vevey, prendre rendez-vous Riviera, téléphone Nutri Centre, Clarens Montreux"
        url="https://nutricentreriviera.ch/contact"
      />
      {/* Hero Section */}
      <section
        style={{
          background: 'var(--gradient-hero)',
          padding: '6rem 0 4rem',
          textAlign: 'center'
        }}
      >
        <div className="container">
          <h1 className="heading-hero" style={{ marginBottom: '1.5rem' }}>
            Contactez-nous
          </h1>
          <p
            className="heading-subtitle"
            style={{ maxWidth: '700px', margin: '0 auto' }}
          >
            Notre équipe est à votre écoute pour répondre à toutes vos questions
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="section-lg" style={{ background: 'white' }}>
        <div className="container">
          <div
            data-testid="contact-info-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
              gap: '1.5rem',
              marginBottom: '3.5rem'
            }}
          >
            {contactCards.map((card, i) => (
              <div
                key={i}
                data-testid={card.testId}
                className="contact-card"
                style={{
                  background: 'var(--bg-page)',
                  borderRadius: '1rem',
                  padding: '2rem',
                  border: '1px solid var(--border-light)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '1rem',
                  transition: 'all 0.3s ease'
                }}
              >
                <div
                  style={{
                    width: '3rem',
                    height: '3rem',
                    borderRadius: '0.75rem',
                    background: 'var(--accent-primary-light)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--accent-primary)'
                  }}
                >
                  {card.icon}
                </div>
                <div>
                  <h4 className="heading-3" style={{ marginBottom: '0.5rem' }}>
                    {card.title}
                  </h4>
                  {card.content}
                </div>
              </div>
            ))}
          </div>

          {/* Interactive Google Map */}
          <div
            data-testid="contact-google-map"
            style={{
              height: '450px',
              borderRadius: '1rem',
              overflow: 'hidden',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              border: '1px solid var(--border-light)'
            }}
          >
            <iframe
              title="Cabinet Nutri Centre Riviera - Vevey"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2747.4!2d6.8419!3d46.4597!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x478ea0f3e1234567%3A0x1234567890abcdef!2sAv.%20du%20G%C3%A9n%C3%A9ral-Guisan%2026%2C%201800%20Vevey!5e0!3m2!1sfr!2sch!4v1234567890123!5m2!1sfr!2sch"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>

      {/* CTA OneDoc */}
      <section
        data-testid="contact-onedoc-cta"
        style={{
          background: 'var(--accent-primary)',
          color: 'white',
          padding: '4.5rem 0'
        }}
      >
        <div className="container" style={{ textAlign: 'center' }}>
          <h2 className="heading-1" style={{ color: 'white', marginBottom: '1rem' }}>
            Prendre directement rendez-vous en ligne
          </h2>
          <p
            className="body-large"
            style={{
              color: 'white',
              opacity: 0.95,
              maxWidth: '640px',
              margin: '0 auto 2.5rem'
            }}
          >
            Réservez votre créneau en 2 clics via notre plateforme OneDoc, 24h/24 et 7j/7.
          </p>
          <a
            href="https://www.onedoc.ch/fr/cabinet-de-groupe/vevey/ebdj4/nutri-centre-riviera"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary"
            style={{
              background: 'white',
              color: 'var(--accent-primary)',
              textDecoration: 'none',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
            }}
            data-testid="contact-onedoc-btn"
          >
            Réserver sur OneDoc
            <ArrowRight size={20} />
          </a>
        </div>
      </section>

      <style>{`
        .contact-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 24px rgba(61, 84, 70, 0.15);
          border-color: var(--accent-primary);
        }
      `}</style>
    </div>
  );
};

export default ContactPage;
